// React
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import Skeleton from 'ui/blocks/Skeleton'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import { fetchPrice, fetchPrices } from 'shop-components/redux/actions/price'
import { money } from 'shared/utils/format'
import { withTranslation } from 'shared/utils/withTranslation'
import { hasVariations, isValidBundleVariations } from 'shop-components/utils/variations'

const ProductPrice = ({
  productId,
  isLoading,
  price,
  defaultPrice,
  cart,
  product,
  fetchProductPrice,
  fetchProductPrices,
  currency,
  t
}) => {
  useEffect(() => {
    if (product?.id && cart?.id && (product?.variation_id || product?.configuration)) {
      return fetchProductPrice({
        cartId: cart.id,
        id: product.variation_id || product.id,
        configuration: product.configuration
      })
    }

    if (productId && cart?.id) {
      fetchProductPrices({
        cartId: cart.id,
        id: productId
      })
    }
  }, [productId, product?.id, product?.variation_id, product?.configuration, cart.id, cart.startsAt, cart.stopsAt])

  if (isLoading) return <Skeleton width={100} />

  if (product?.type !== 'bundles' && hasVariations(product) && (!product.variation_id || product.variation_id === '')) {
    return `${t('user.store.price_from')} ${money(price, currency)}`
  }

  if (hasVariations(product) && !isValidBundleVariations(product)) {
    return `${t('user.store.price_from')} ${money(price, currency)}`
  }

  if (price) return money(price, currency)

  if (defaultPrice) return defaultPrice

  return null
}

ProductPrice.displayName = 'ProductPrice'

ProductPrice.propTypes = {
  productId: PropTypes.string,
  product: PropTypes.object,
  price: PropTypes.number,
  isLoading: PropTypes.bool,
  defaultPrice: PropTypes.string,
  cart: PropTypes.object,
  fetchProductPrice: PropTypes.func,
  fetchProductPrices: PropTypes.func,
  currency: PropTypes.object,
  t: PropTypes.func
}

const mapStateToProps = (state, props) => {
  const cart = state.orm.carts.getById(state.client.cartId || window.booqableOptions.cart?.id)
  const product = state.items.records[props.productId]
  const price = product?.price?.price_each_in_cents

  return {
    cart: {
      id: cart?.id ?? window.booqableOptions.cart?.id,
      startsAt: cart?.starts_at ?? window.booqableOptions.cart?.from,
      stopsAt: cart?.stops_at ?? window.booqableOptions.cart?.till
    },
    currency: window.booqableOptions.currency,
    isLoading: state.items.loading && !price,
    price,
    product
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchProductPrice: (attributes) => dispatch(fetchPrice(attributes)),
  fetchProductPrices: (attributes) => dispatch(fetchPrices(attributes))
})

const decoratedComponent = compose(
  withStore,
  withTheme,
  withTranslation('user'),
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(ProductPrice)

registerCustomElement('bq-product-price', decoratedComponent, {
  mountElement: 'span',
  attributes: ['product-id', 'default-price']
})

export default decoratedComponent
